/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useLockBodyScroll } from "react-use";
import { Sidebar, Button, theme } from "@nordcloud/gnui";
import { DiscoveryRule } from "~/generated/graphql";
import { mapRules } from "~/hooks/useDiscoveryRules/utils";
import { ACTIONS, useAccess } from "~/services/auth";
import { isNotEmpty, isEmpty, getLastItem, isEmptyObj } from "~/tools";
import { FORM_TITLE, EMPTY_RULE, READ_ONLY_FORM_TITLE } from "./constants";
import { FormContextProvider, useFormContext } from "./context";
import { DiscoveryRuleItem } from "./DiscoveryRuleItem";

type Props = {
  onSubmit: (data: DiscoveryRule[]) => void;
  isOpen: boolean;
  onClose: () => void;
  rules?: DiscoveryRule[];
};

function Form({ onSubmit, isOpen, onClose }: Props) {
  const { discoveryRules, addRule } = useFormContext();

  const { hasAccess: editable } = useAccess(ACTIONS.updateDiscoveryRules);

  // Fixes issue with flickering when there are a lot of rules being displayed
  useLockBodyScroll(isOpen);

  const handleSubmit = () => {
    onSubmit(mapRules(discoveryRules));
  };

  React.useEffect(() => {
    // Make sure there is at least 1 empty rule displayed, so the user sees a select input
    if (isEmpty(discoveryRules)) {
      addRule(EMPTY_RULE);
    }
  }, [discoveryRules, addRule]);

  // Buttons are shown when there is at least 1 non-empty rule
  const showButtons =
    editable &&
    isNotEmpty(
      Object.keys(getLastItem(discoveryRules) ?? {}).filter(
        (prop) => prop !== "id"
      )
    );

  return (
    <Sidebar
      title={editable ? FORM_TITLE : READ_ONLY_FORM_TITLE}
      isOpen={isOpen}
      onClick={onClose}
      css={{
        textAlign: "left",
      }}
      footer={
        showButtons ? (
          <>
            <Button
              margin={theme.spacing.spacing04}
              type="button"
              onClick={handleSubmit}
            >
              Apply Settings
            </Button>
            <Button
              margin={theme.spacing.spacing04}
              type="button"
              severity="medium"
              onClick={() => addRule(EMPTY_RULE)}
              icon="plus"
              disabled={isEmptyObj(getLastItem(discoveryRules).match)}
            >
              Add rule
            </Button>
          </>
        ) : null
      }
    >
      {discoveryRules.map((rule, index) => (
        <DiscoveryRuleItem
          key={rule.id}
          title={`#${index + 1} Discovery Rule`}
          rule={rule}
          editable={editable}
          removable={index !== 0}
        />
      ))}
    </Sidebar>
  );
}

export function DiscoveryRulesForm({ rules = [], ...props }: Props) {
  return (
    <FormContextProvider initialRules={rules}>
      <Form {...props} />
    </FormContextProvider>
  );
}
